import type { ChecklistItem } from '@/store/tasks/tasks.dto';

/**
 * A step while it is being written. `draftId` is the editor's own, for keys and dragging; a new
 * step has no service `id` yet.
 */
export type ChecklistDraft = ChecklistItem & { draftId: string };

let lastDraftId = 0;
export const newDraft = (item: ChecklistItem = { text: '', status: 'open' }): ChecklistDraft => ({
  ...item,
  draftId: String(++lastDraftId),
});

/**
 * `previous` hands its keys on to the same steps (matched by the service's id), so a list
 * refreshed from the service keeps its rows, and a rename open on one, in place.
 */
export const toDrafts = (
  items: ChecklistItem[] | null | undefined,
  previous: ChecklistDraft[] = [],
): ChecklistDraft[] => {
  const keys = new Map(previous.flatMap((d) => (d.id ? [[d.id, d.draftId] as const] : [])));
  return (items ?? []).map((item) => {
    const draftId = item.id && keys.get(item.id);
    return draftId ? { ...item, draftId } : newDraft(item);
  });
};

/** What a save sends: trimmed, blank steps dropped, the editor's ids gone, the service's kept. */
export const fromDrafts = (drafts: ChecklistDraft[] | undefined): ChecklistItem[] =>
  (drafts ?? []).flatMap(({ id, text, status }) =>
    text.trim() ? [{ id, text: text.trim(), status }] : [],
  );
